import {
    type AnyPermission,
    PermissionHint,
    PermissionTitle,
    type Section,
    type TeamPermissions,
} from "@qendo/permissions";
import { View } from "react-native";
import { Subtitle, Title } from "~/components/layout/text";
import { FieldHint } from "~/components/layout/text/FieldHint";
import { Nothing } from "~/components/permission/Nothing";
import { Only } from "~/components/permission/Only";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items } from "~/constants/layout";
import { useTheme } from "~/providers/ThemeProvider";
import { hasAnyAbility } from "~/util/qendo/team";

export type PermissionItemProps = {
    section: Section;
    title?: string;
    hint?: string;
    hideEmpty?: boolean;
    permission: AnyPermission;
};

const ICON_SIZE = 16;
export const PermissionItem = ({ section, title, hint, permission, hideEmpty }: PermissionItemProps) => {
    const { colours } = useTheme();
    const has_any = hasAnyAbility(permission);
    const field_hint = hint ?? PermissionHint[section];
    const permission_title = title ?? PermissionTitle[section];

    if (hideEmpty && !has_any) {
        return <Nothing />;
    }

    return (
        <Only when={has_any || !hideEmpty}>
            <View style={[Flex.row, Gap.column_sm, Flex.between]}>
                <View style={[Gap.row_xxx, Flex.grow]}>
                    <Title size="base">{permission_title}</Title>
                    {field_hint && <FieldHint>{field_hint}</FieldHint>}
                </View>
                <View style={[Flex.row, Gap.column_xs, Items.center]}>
                    <Only when={has_any}>
                        <Subtitle>
                            {((permission as TeamPermissions).assign_owner && "Update owner") ||
                                (permission.update && "Update") ||
                                "View"}
                        </Subtitle>
                        <QendoIcon
                            name={permission.update ? "edit" : "view-on"}
                            size={ICON_SIZE}
                            color={colours.icon.primary}
                        />
                    </Only>
                    <Only not when={has_any}>
                        <Subtitle>No access</Subtitle>
                        <QendoIcon name="denied" size={ICON_SIZE} color={colours.icon.secondary} />
                    </Only>
                </View>
            </View>
        </Only>
    );
};
